import type {
    CategoryFilterState,
    CategoryTree,
} from '@rocco/ui/filter-sort/view-models/filter-state/category-filter-state';

const normaliseUrl = (url: string): string => url.replace(/\/+$/, '');

/**
 * Depth-first search for the node with the given canonical URL.
 */
export const findCategoryNode = (
    root: CategoryTree,
    url: string,
): CategoryTree | null => {
    if (normaliseUrl(root.url) === normaliseUrl(url)) {
        return root;
    }

    for (const child of root.children) {
        const found = findCategoryNode(child, url);
        if (found) return found;
    }

    return null;
};

/**
 * The ancestor chain `[root, …, node]` for the node with the given URL, or
 * an empty array when the URL is not in the tree.
 */
export const getCategoryTrail = (
    root: CategoryTree,
    url: string,
): CategoryTree[] => {
    if (normaliseUrl(root.url) === normaliseUrl(url)) {
        return [root];
    }

    for (const child of root.children) {
        const trail = getCategoryTrail(child, url);
        if (trail.length > 0) {
            return [root, ...trail];
        }
    }

    return [];
};

/**
 * Stage the category with the given URL as the pending selection. The root
 * node stages as an explicit selection too, so a commit emits the vertical
 * root path rather than falling back to the current page's path. Returns
 * null when the URL is not in the tree.
 */
export const stageCategorySelection = (
    state: CategoryFilterState,
    url: string,
): CategoryFilterState | null => {
    const node = findCategoryNode(state.category, url);
    if (!node) return null;

    return {
        ...state,
        selectedCategory: node,
        isRoot: normaliseUrl(node.url) === normaliseUrl(state.category.url),
    };
};

/**
 * The category a commit would move to, or null when the staged selection is
 * still the page's own category (or nothing is staged). The committed state's
 * selection falls back to its tree root, which is where the page sits when
 * nothing below the root is selected.
 */
export const getStagedCategoryChange = (
    committed: CategoryFilterState,
    staged: CategoryFilterState,
): CategoryTree | null => {
    const next = staged.selectedCategory;
    if (!next) return null;

    const current = committed.selectedCategory ?? committed.category;
    return normaliseUrl(next.url) === normaliseUrl(current.url) ? null : next;
};
